import { useEffect } from 'react';

type UseOnDetectOutsideClickProps = {
	/**
	 * Qualifiers are used to determine if the outside click should be detected.
	 * If all qualifiers are true, the outside click will be detected.
	 */
	qualifiers: boolean[];
	/**
	 * If any of the refs contain the event target, the outside click will not be detected.
	 */
	refs: React.RefObject<HTMLElement>[];
	/**
	 * onOutsideClick is the function that will be called when the outside click is detected.
	 */
	onOutsideClick: () => void;
};

export const useOnDetectOutsideClick = ({
	qualifiers,
	refs,
	onOutsideClick,
}: UseOnDetectOutsideClickProps) => {
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				qualifiers.every((qualifier) => qualifier) &&
				!refs.some((ref) => ref?.current?.contains(event.target as Node))
			) {
				onOutsideClick();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [qualifiers, refs, onOutsideClick]);
};
